import api, { handleApiError } from './api';
import { AccessContext } from '../types/auth';

export interface DirectoryUser {
  id: number;
  display_name: string;
  username: string;
  department: string;
  plant: string;
  // Compatibility fields used by existing participant/mentor controls.
  full_name: string;
  first_name: string;
  last_name: string;
}

interface DirectoryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Array<{
    id: number;
    display_name: string;
    username: string;
    department: string;
    plant: string;
  }>;
}

const normalizeDirectoryUser = (user: DirectoryResponse['results'][number]): DirectoryUser => {
  const displayName = user.display_name || user.username;
  const nameParts = displayName.trim().split(/\s+/);
  return {
    ...user,
    full_name: displayName,
    first_name: nameParts[0] || '',
    last_name: nameParts.slice(1).join(' '),
  };
};

export class AccessService {
  static async getContext(): Promise<AccessContext> {
    try {
      const response = await api.get<AccessContext>('/access/context/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  static async getDirectoryUsers(filters: {
    search?: string;
    plant?: string;
    page?: number;
    page_size?: number;
  } = {}): Promise<{ count: number; next: string | null; previous: string | null; results: DirectoryUser[] }> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value));
        }
      });
      const response = await api.get<DirectoryResponse>(`/directory/users/?${params.toString()}`);
      return {
        ...response.data,
        results: response.data.results.map(normalizeDirectoryUser),
      };
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  static async getAllDirectoryUsers(filters: Omit<Parameters<typeof AccessService.getDirectoryUsers>[0], 'page'> = {}): Promise<DirectoryUser[]> {
    const users: DirectoryUser[] = [];
    let page = 1;
    while (true) {
      const response = await this.getDirectoryUsers({ ...filters, page, page_size: 100 });
      users.push(...response.results);
      if (!response.next) return users;
      page += 1;
    }
  }
}

